import React, { useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  EnvelopeIcon,
  UsersIcon,
  PhoneIcon,
  TrophyIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { ConversationAnalytics, VisitorJourneyData } from '../../services/analyticsService';

interface ContextTrackerProps {
  conversation: ConversationAnalytics;
  visitorJourney: VisitorJourneyData;
}

interface ContextStep {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  status: 'pending' | 'completed' | 'failed';
  messageIndex?: number;
  value?: string;
  timestamp?: string;
  description: string;
}

const ContextTracker: React.FC<ContextTrackerProps> = ({ conversation, visitorJourney }) => {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);

  // Parse conversation context
  const context = conversation.context || {};
  const messages = conversation.messages || [];

  // Analyze context evolution through messages
  const analyzeContextEvolution = () => {
    const steps: ContextStep[] = [
      {
        id: 'name',
        label: 'Parent Name',
        icon: UserIcon,
        status: conversation.has_name ? 'completed' : 'pending',
        value: context.parentName || visitorJourney.lead?.parent_name,
        description: 'Parent provides their full name for personalization'
      },
      {
        id: 'email',
        label: 'Email Address',
        icon: EnvelopeIcon,
        status: conversation.has_email ? 'completed' : 'pending',
        value: context.email || conversation.visitor_email || conversation.lead_email,
        description: 'Email address for sending resources and follow-up'
      },
      {
        id: 'child_info',
        label: 'Child Information',
        icon: UsersIcon,
        status: conversation.has_child_info ? 'completed' : 'pending',
        value: context.children?.length ? `${context.children.length} child(ren)` : context.childName,
        description: 'Child\'s name, age, and special needs information'
      },
      {
        id: 'phone_consent',
        label: 'Phone Consent',
        icon: PhoneIcon,
        status: context.phoneConsent ? 'completed' : 'pending',
        value: context.phoneConsent ? 'Granted' : undefined,
        description: 'Consent to receive text updates and phone calls'
      }
    ];

    // Find when each step was completed by analyzing messages
    messages.forEach((message: any, index: number) => {
      if (message.role === 'user') {
        const content = message.content.toLowerCase();

        // Name detection
        if (!steps[0].messageIndex && /\b(my name is|i'm|call me)\s+([a-z]+)/i.test(message.content)) {
          steps[0].messageIndex = index;
          steps[0].timestamp = message.timestamp;
        }

        // Email detection
        if (!steps[1].messageIndex && /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/.test(message.content)) {
          steps[1].messageIndex = index;
          steps[1].timestamp = message.timestamp;
        }

        // Child info detection
        if (!steps[2].messageIndex && /\b(son|daughter|child|kid|boy|girl|he|she|they|age|years old|grade)\b/i.test(content)) {
          steps[2].messageIndex = index;
          steps[2].timestamp = message.timestamp;
        }

        // Phone consent detection
        if (!steps[3].messageIndex && /\b(yes|sure|okay|ok|sounds good|that works)\b/i.test(content) &&
            (/\b(phone|text|call|number)\b/i.test(content) || /(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}/.test(message.content))) {
          steps[3].messageIndex = index;
          steps[3].timestamp = message.timestamp;
        }
      }
    });

    return steps;
  };

  const steps = analyzeContextEvolution();
  const completedSteps = steps.filter(step => step.status === 'completed').length;
  const totalSteps = steps.length;
  const completionPercentage = Math.round((completedSteps / totalSteps) * 100);

  const toggleStep = (stepId: string) => {
    setExpandedStep(expandedStep === stepId ? null : stepId);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      month: 'short',
      day: 'numeric',
      timeZoneName: 'short'
    });
  };

  return (
    <div>
      {/* Progress Overview */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-medium text-gray-900">Context Evolution</h4>
            <p className="text-sm text-gray-500">
              Information collection progress throughout the conversation
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {conversation.lead_id && (
              <div className="flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                <TrophyIcon className="h-4 w-4 mr-1" />
                Lead Created
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-gray-200 rounded-full h-3 mb-2">
          <div
            className={`h-3 rounded-full transition-all duration-500 ${
              completionPercentage === 100 ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <span>{completedSteps} of {totalSteps} steps completed</span>
          <span>{completionPercentage}% complete</span>
        </div>
      </div>

      {/* Context Steps */}
      <div className="space-y-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isExpanded = expandedStep === step.id;
          const isCompleted = step.status === 'completed';

          return (
            <div
              key={step.id}
              className={`border rounded-lg transition-all duration-200 ${
                isCompleted
                  ? 'border-green-200 bg-green-50'
                  : 'border-gray-200 bg-white'
              } ${isExpanded ? 'shadow-md' : 'shadow-sm'}`}
            >
              <div
                className="p-4 cursor-pointer"
                onClick={() => toggleStep(step.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {/* Step Number/Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}>
                      {isCompleted ? (
                        <CheckCircleIcon className="h-5 w-5" />
                      ) : (
                        <span className="text-sm font-medium">{index + 1}</span>
                      )}
                    </div>

                    {/* Step Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-2">
                        <Icon className="h-5 w-5 text-gray-400" />
                        <h5 className="text-sm font-medium text-gray-900">{step.label}</h5>
                        {step.value && (
                          <span className="text-sm text-gray-600">• {step.value}</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{step.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {step.timestamp && (
                      <div className="flex items-center text-xs text-gray-500">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {formatTimestamp(step.timestamp)}
                      </div>
                    )}
                    {isExpanded ? (
                      <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-200">
                  <div className="pt-4">
                    {step.messageIndex !== undefined ? (
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900">
                              Message #{step.messageIndex + 1}
                            </span>
                            <span className="text-xs text-gray-500">
                              {step.timestamp && formatTimestamp(step.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700">
                            {messages[step.messageIndex]?.content || 'Message content not available'}
                          </p>
                        </div>

                        {step.id === 'child_info' && context.children && (
                          <div className="bg-blue-50 rounded-lg p-3">
                            <h6 className="text-sm font-medium text-blue-900 mb-2">Children Information</h6>
                            {context.children.map((child: any, childIndex: number) => (
                              <div key={childIndex} className="text-sm text-blue-800 mb-1">
                                • {child.name} (Age: {child.age || 'Not specified'})
                                {child.specialNeeds && (
                                  <span className="text-orange-600 ml-2">
                                    - {Array.isArray(child.specialNeeds) ? child.specialNeeds.join(', ') : child.specialNeeds}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {step.id === 'phone_consent' && context.phoneConsent && (
                          <div className="bg-green-50 rounded-lg p-3">
                            <div className="flex items-center text-sm text-green-800">
                              <CheckCircleIcon className="h-4 w-4 mr-2" />
                              Parent consented to receive text updates and phone calls
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <XCircleIcon className="h-4 w-4 mr-2" />
                          This information was not collected during the conversation
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Context Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <h5 className="text-sm font-medium text-gray-900 mb-3">Context Summary</h5>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-gray-900">Visitor Status</p>
              <p className="text-gray-600">
                {context.parentStatus || 'New parent exploring school options'}
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-900">Timeline</p>
              <p className="text-gray-600">
                {context.timeline || visitorJourney.lead?.timeline || 'Not specified'}
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-900">Primary Concerns</p>
              <p className="text-gray-600">
                {context.biggestConcerns || 'Not specified'}
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-900">Lead Score</p>
              <p className="text-gray-600">
                {visitorJourney.lead?.lead_score || 0}/100
              </p>
            </div>
          </div>

          {/* Full Context Data (Expandable) */}
          <details className="mt-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800">
              View Raw Context Data
            </summary>
            <pre className="mt-2 text-xs bg-gray-100 p-3 rounded overflow-x-auto">
              {JSON.stringify(context, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
};

export default ContextTracker;